// AIToolsSection.jsx - Updated to include Video Generation tool

import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  LinearProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  VolumeUp as AudioIcon,
  Description as DocumentIcon,
  AutoAwesome as AIIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Translate as TranslateIcon,
  Storage as StorageIcon,
  // Voice Cloning Icons
  RecordVoiceOver as RecordVoiceOverIcon,
  VolumeUp as VolumeUpIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  // NEW: Video Generation Icons
  VideoCall as VideoIcon,
  Movie as MovieIcon,
  Videocam as VideocamIcon,
  PlayCircle as PlayCircleIcon,
  VideoLibrary as VideoLibraryIcon,
  OpenInNew as OpenInNewIcon,
  MenuBook as KnowledgeIcon,
  RateReview as DraftsIcon
} from '@mui/icons-material';

import BusinessAudioUpload from './BusinessAudioUpload';
import BusinessDocumentUpload from './BusinessDocumentUpload';
import { useGetUserProfileQuery } from '../../redux/thunks/users/userprofile';
import SocialMediaManager from './SocialMediaManager';
import AnnouncementBox from '../Announcements/AnnouncementBox';
import { Campaign as CampaignIcon } from '@mui/icons-material';
import { useGetDraftsQuery } from '../../redux/thunks/drafts/getdrafts';
import usePollingInterval from '../../hooks/usePollingInterval';



// Import Voice Cloning Component
import VoiceCloningTool from './VoiceCloningTool';

// Document quota constants
const DOCUMENT_QUOTAS = {
  free: {
    maxDocuments: 1,
    maxFileSize: 5 * 1024 * 1024, // 5MB per file
    maxTotalStorage: 5 * 1024 * 1024 // 5MB total
  },
  premium: {
    maxDocuments: 20,
    maxFileSize: 50 * 1024 * 1024, // 50MB per file
    maxTotalStorage: 500 * 1024 * 1024 // 500MB total
  }
};

// Voice quota constants
const VOICE_QUOTAS = {
  free: {
    maxVoiceSamples: 1,
    maxTTSCharacters: 500, // per month
    maxSampleDuration: 120, // 2 minutes
    maxFileSize: 10 * 1024 * 1024 // 10MB
  },
  premium: {
    maxVoiceSamples: 5,
    maxTTSCharacters: 5000, // per month
    maxSampleDuration: 300, // 5 minutes
    maxFileSize: 50 * 1024 * 1024 // 50MB
  },
  enterprise: {
    maxVoiceSamples: -1, // unlimited
    maxTTSCharacters: 20000, // per month
    maxSampleDuration: 600, // 10 minutes
    maxFileSize: 100 * 1024 * 1024 // 100MB
  }
};

// NEW: Video Generation quota constants (for future implementation)
const VIDEO_QUOTAS = {
  free: {
    maxVideos: 1, // per month
    maxDuration: 15, // 30 seconds
    maxResolution: '720p',
    maxFileSize: 50 * 1024 * 1024 // 50MB
  },
  premium: {
    maxVideos: 5, // per month
    maxDuration: 30, // 2 minutes
    maxResolution: '720p',
    maxFileSize: 200 * 1024 * 1024 // 200MB
  },
  enterprise: {
    maxVideos: -1, // unlimited
    maxDuration: 600, // 10 minutes
    maxResolution: '4K',
    maxFileSize: 1024 * 1024 * 1024 // 1GB
  }
};

const AIToolsSection = ({ 
  businessId, 
  businessName, 
  isBusinessOwner, 
  accountType = 'free',
  existingAudioDescription = null,
  existingDocuments = [],
  // Voice-related props
  voiceLibrary = null,
  quotaUsage = null,
  existingVoice = null,
  googlePlaceId = null
}) => {
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth?.userId || null);
  
  // Get user data for quota checking
  const { data: userData, isError: userProfileError, isLoading: isUserLoading } = useGetUserProfileQuery(userId);
  const pendingPoll = usePollingInterval(60000);
  const { data: pendingDraftsData } = useGetDraftsQuery(
  { businessId, status: 'pending_approval' },
  { skip: !isBusinessOwner || !businessId, pollingInterval: pendingPoll }
  );

  const pendingDraftCount = pendingDraftsData?.total || 0;


  const [openDialog, setOpenDialog] = useState(null);
  const [audioData, setAudioData] = useState(existingAudioDescription);
  const [documents, setDocuments] = useState(existingDocuments);
  // Voice state
  const [voice, setVoice] = useState(existingVoice);

  // Get quota limits based on account type
  const documentQuotas = DOCUMENT_QUOTAS[accountType] || DOCUMENT_QUOTAS.free;
  const voiceQuotas = VOICE_QUOTAS[accountType] || VOICE_QUOTAS.free;
  const videoQuotas = VIDEO_QUOTAS[accountType] || VIDEO_QUOTAS.free; // NEW: Video quotas
  const isPremium = accountType === 'premium';
  const isEnterprise = accountType === 'enterprise';

  // Get current month key (format: YYYY-M to match DB structure)
  const currentMonthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}`;
  }, []);

  console.log('quotaUsage', quotaUsage)

  // Voice usage calculations
  const voiceUsageData = useMemo(() => {
    if (!quotaUsage) {
      return {
        samplesCreated: voiceLibrary?.voices?.length || 0,
        ttsCharactersUsed: 0,
        monthlyTtsUsage: 0
      };
    }

    // Voice cloning quota (for voice samples)
    const voiceQuota = quotaUsage.voice_cloning || {};
    
    // TTS generation quota (for character usage)
    const ttsQuota = quotaUsage.voice_tts_generation || {};
    
    return {
      samplesCreated: voiceLibrary?.voices?.length || 0,
      ttsCharactersUsed: ttsQuota.lifetime || ttsQuota.totalCharacters || 0,
      monthlyTtsUsage: ttsQuota.monthly?.[currentMonthKey] || ttsQuota.monthly?.[`${currentMonthKey}_characters`] || 0
    };
  }, [quotaUsage, voiceLibrary, currentMonthKey]);

  // NEW: Video usage calculations (placeholder for future implementation)
  const videoUsageData = useMemo(() => {
    if (!quotaUsage) {
      return {
        videosGenerated: 0,
        monthlyVideoUsage: 0
      };
    }

    // Video generation quota (for future implementation)
    const videoQuota = quotaUsage.video_generation || {};
    
    return {
      videosGenerated: videoQuota.lifetime || 0,
      monthlyVideoUsage: videoQuota.monthly?.[currentMonthKey] || 0
    };
  }, [quotaUsage, currentMonthKey]);

  // Calculate current document usage from quota data
  const currentDocumentUsage = useMemo(() => {
    if (!userData?.quotaUsage) {
      // Fallback to existing documents data
      const documentCount = documents.length;
      const storageUsed = documents.reduce((total, doc) => total + (doc.size || 0), 0);
      
      return {
        documentsUploaded: documentCount,
        storageUsedBytes: storageUsed
      };
    }

    // Get document upload count from quota data
    const documentUploads = userData.quotaUsage.document_uploads?.monthly?.[currentMonthKey] || 0;
    
    // Get storage usage (convert from MB to bytes)
    const storageUsedMB = userData.quotaUsage.document_storage?.monthly?.[currentMonthKey] || 0;
    const storageUsedBytes = storageUsedMB * 1024 * 1024;

    return {
      documentsUploaded: documentUploads,
      storageUsedBytes: storageUsedBytes
    };
  }, [userData, currentMonthKey, documents]);

  // Calculate current usage
  const currentDocumentCount = currentDocumentUsage.documentsUploaded;
  const currentTotalSize = currentDocumentUsage.storageUsedBytes;
  const storageUsedPercent = Math.round((currentTotalSize / documentQuotas.maxTotalStorage) * 100);

  // Voice usage percentages
  const voiceUsagePercent = useMemo(() => {
    const samplesPercent = voiceQuotas.maxVoiceSamples === -1 ? 0 : 
      Math.round((voiceUsageData.samplesCreated / voiceQuotas.maxVoiceSamples) * 100);
    
    const ttsPercent = Math.round((voiceUsageData.monthlyTtsUsage / voiceQuotas.maxTTSCharacters) * 100);

    return {
      samples: samplesPercent,
      tts: ttsPercent
    };
  }, [voiceUsageData, voiceQuotas]);

  // NEW: Video usage percentages (for future implementation)
  const videoUsagePercent = useMemo(() => {
    return videoQuotas.maxVideos === -1 ? 0 : 
      Math.round((videoUsageData.monthlyVideoUsage / videoQuotas.maxVideos) * 100);
  }, [videoUsageData, videoQuotas]);

   // Helper function to check if voice features are available
   const hasVoiceCapabilities = () => {
    return voiceLibrary && voiceLibrary.voices && voiceLibrary.voices.length > 0;
  };

  // Availability checks
  const canCreateVoice = voiceQuotas.maxVoiceSamples === -1 || voiceUsageData.samplesCreated < voiceQuotas.maxVoiceSamples;
  const canUseTTS = voiceUsageData.monthlyTtsUsage < voiceQuotas.maxTTSCharacters;
  const hasActiveVoices = hasVoiceCapabilities() && voiceLibrary.voices.some(v => v.status === 'active');

  const handleCloseDialog = () => {
    setOpenDialog(null);
  };

  const handleAudioSuccess = (newAudioData) => {
    setAudioData(newAudioData);
    setOpenDialog(null);
  };

  const handleDocumentSuccess = (result) => {
    if (result.action === 'delete') {
      // Handle document deletion
      setDocuments(prev => prev.filter((_, index) => index !== result.deletedIndex));
    } else if (Array.isArray(result)) {
      // Handle new document uploads (array of documents)
      setDocuments(prev => [...prev, ...result]);
    } else {
      // Handle single document upload
      setDocuments(prev => [...prev, result]);
    }
    setOpenDialog(null);
  };

  // Voice cloning success handler
  const handleVoiceSuccess = (newVoiceData) => {
    setVoice(newVoiceData);
    setOpenDialog(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Get active voice summary
  const getVoiceSummary = () => {
    if (!hasVoiceCapabilities()) {
      return null;
    }

    const activeVoices = voiceLibrary.voices.filter(v => v.status === 'active');
    const processingVoices = voiceLibrary.voices.filter(v => v.status === 'processing');
    
    return {
      total: voiceLibrary.voices.length,
      active: activeVoices.length,
      processing: processingVoices.length,
      mostRecent: voiceLibrary.voices[0] // Assuming they're sorted by creation date
    };
  };

  // Don't render if not business owner
  if (!isBusinessOwner) return null;

  const voiceSummary = getVoiceSummary();

  return (
    <Paper variant="outlined" sx={{ 
        p: 2, 
        mb: 2, 
        border: '3px solid', 
        borderColor: 'primary.main',
        background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)',
        borderRadius: 2
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AIIcon color="primary" />
        <Typography variant="subtitle2">AI Business Tools</Typography>
        <Chip 
          label="BETA" 
          size="small" 
          color="primary" 
          variant="outlined" 
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enhance your business profile with AI-powered tools
      </Typography>

      {/* Tell us what's happening: open by default, it's the main thing to do here */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <CampaignIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="600">Tell us what's happening</Typography>
            <IconButton
              size="small"
              sx={{ ml: 'auto' }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/business/${businessId}/social/content`,
                         { state: { businessName, returnPath: window.location.pathname } });
              }}
              title="See your updates"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <AnnouncementBox businessId={businessId} compact />
        </AccordionDetails>
      </Accordion>

      {/* Voice Cloning Accordion */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <RecordVoiceOverIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">Voice Cloning</Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {voiceSummary && (
                <>
                  <Chip 
                    label={`${voiceSummary.active} Active`}
                    size="small" 
                    color="success"
                    variant="outlined"
                  />
                  {voiceSummary.processing > 0 && (
                    <Chip 
                      label={`${voiceSummary.processing} Processing`}
                      size="small" 
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </>
              )}
              {hasVoiceCapabilities() && (
                <CheckIcon color="success" fontSize="small" />
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create AI voice clones for text-to-speech generation. Perfect for customer service, announcements, and marketing content.
          </Typography>
          
          {/* Voice Usage Summary */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="caption" fontWeight="medium" sx={{ mb: 1, display: 'block' }}>
              Voice Usage Summary
            </Typography>
            
            <Stack spacing={1}>
              {/* Voice Samples Usage */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption">Voice Samples</Typography>
                  <Typography variant="caption">
                    {voiceUsageData.samplesCreated}/{voiceQuotas.maxVoiceSamples === -1 ? '∞' : voiceQuotas.maxVoiceSamples}
                  </Typography>
                </Box>
                {voiceQuotas.maxVoiceSamples !== -1 && (
                  <LinearProgress 
                    variant="determinate" 
                    value={voiceUsagePercent.samples} 
                    size="small"
                    color={voiceUsagePercent.samples > 90 ? "error" : "primary"}
                  />
                )}
              </Box>

              {/* TTS Characters Usage */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption">TTS Characters (This Month)</Typography>
                  <Typography variant="caption">
                    {voiceUsageData.monthlyTtsUsage.toLocaleString()}/{voiceQuotas.maxTTSCharacters.toLocaleString()}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={voiceUsagePercent.tts} 
                  size="small"
                  color={voiceUsagePercent.tts > 90 ? "error" : "success"}
                />
              </Box>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {accountType.charAt(0).toUpperCase() + accountType.slice(1)} Plan • 
              {voiceQuotas.maxVoiceSamples === -1 ? ' Unlimited samples' : ` ${voiceQuotas.maxVoiceSamples} samples`} • 
              {voiceQuotas.maxTTSCharacters.toLocaleString()} TTS chars/month
            </Typography>
          </Box>

          {/* Voice Status Display */}
          {hasVoiceCapabilities() ? (
            <Box sx={{ mb: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Voice Library Active!</strong> You have {voiceSummary.active} voice{voiceSummary.active !== 1 ? 's' : ''} ready for TTS generation.
                  {voiceSummary.processing > 0 && ` ${voiceSummary.processing} voice${voiceSummary.processing !== 1 ? 's' : ''} currently processing.`}
                </Typography>
              </Alert>
              
              {/* TTS Availability Status */}
              {hasActiveVoices && canUseTTS && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>TTS Ready!</strong> You can generate speech using your active voices. 
                    {voiceQuotas.maxTTSCharacters - voiceUsageData.monthlyTtsUsage} characters remaining this month.
                  </Typography>
                </Alert>
              )}
              
              {hasActiveVoices && !canUseTTS && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>TTS Limit Reached!</strong> You've used all {voiceQuotas.maxTTSCharacters} monthly TTS characters. 
                    {isPremium ? 'Resets next month.' : 'Upgrade to Premium for more characters.'}
                  </Typography>
                </Alert>
              )}
              
              {/* Most Recent Voice Info */}
              {voiceSummary.mostRecent && (
                <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Latest Voice:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {voiceSummary.mostRecent.voiceName}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip 
                      label={voiceSummary.mostRecent.status} 
                      size="small" 
                      color={voiceSummary.mostRecent.status === 'active' ? 'success' : 
                             voiceSummary.mostRecent.status === 'processing' ? 'warning' : 'default'}
                      variant="outlined"
                    />
                    {voiceSummary.mostRecent.provider && (
                      <Chip 
                        label={voiceSummary.mostRecent.provider} 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                No voice samples created yet. Create your first AI voice clone to enable text-to-speech features for your business.
              </Typography>
            </Alert>
          )}

          {/* Action Button */}
          <Button
            variant={hasVoiceCapabilities() ? "outlined" : "contained"}
            onClick={() => setOpenDialog('voice')}
            startIcon={<RecordVoiceOverIcon />}
            disabled={false} // Always allow opening the dialog
            fullWidth
          >
            {hasVoiceCapabilities() ? 
              (hasActiveVoices ? 'Manage Voice Cloning & TTS' : 'Manage Voice Cloning') : 
              'Create Voice Clone'}
          </Button>
          
          {/* Help text based on state */}
          {!isPremium && voiceUsageData.samplesCreated >= voiceQuotas.maxVoiceSamples && voiceQuotas.maxVoiceSamples !== -1 && !hasActiveVoices && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Voice sample limit reached. Upgrade to Premium for more voices.
            </Typography>
          )}
          
          {hasActiveVoices && (
            <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              ✓ Ready for text-to-speech generation
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* NEW: Video Generation Accordion - Coming Soon */}
      <Accordion disabled>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <VideoIcon color="disabled" fontSize="small" />
            <Typography variant="subtitle2" color="text.disabled">Video</Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label="COMING SOON" 
                size="small" 
                color="warning" 
                variant="outlined" 
              />
              <Chip 
                label={`${videoUsageData.monthlyVideoUsage}/${videoQuotas.maxVideos === -1 ? '∞' : videoQuotas.maxVideos}`}
                size="small" 
                color="default"
                variant="outlined"
              />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate professional marketing videos, product showcases, and business introductions using AI. Perfect for social media and website content.
          </Typography>
          
          {/* Video Usage Preview */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, opacity: 0.7 }}>
            <Typography variant="caption" fontWeight="medium" sx={{ mb: 1, display: 'block' }}>
              Monthly Video Generation (Preview)
            </Typography>
            
            <Stack spacing={1}>
              {/* Video Generation Usage */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption">Videos Generated</Typography>
                  <Typography variant="caption">
                    {videoUsageData.monthlyVideoUsage}/{videoQuotas.maxVideos === -1 ? '∞' : videoQuotas.maxVideos}
                  </Typography>
                </Box>
                {videoQuotas.maxVideos !== -1 && (
                  <LinearProgress 
                    variant="determinate" 
                    value={videoUsagePercent} 
                    size="small"
                    color="primary"
                    sx={{ opacity: 0.6 }}
                  />
                )}
              </Box>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {accountType.charAt(0).toUpperCase() + accountType.slice(1)} Plan • 
              {videoQuotas.maxVideos === -1 ? ' Unlimited videos' : ` ${videoQuotas.maxVideos} videos/month`} • 
              Up to {videoQuotas.maxDuration}s duration • {videoQuotas.maxResolution} quality
            </Typography>
          </Box>

          {/* Coming Soon Features */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Coming Soon:</strong> AI-powered video generation with voice integration, automated scene creation, and brand-consistent styling.
            </Typography>
          </Alert>

          {/* Feature Preview */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Planned Features:
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.7 }}>
                <PlayCircleIcon fontSize="small" color="disabled" />
                <Typography variant="caption" color="text.secondary">
                  Business introduction videos
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.7 }}>
                <MovieIcon fontSize="small" color="disabled" />
                <Typography variant="caption" color="text.secondary">
                  Product showcase reels
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.7 }}>
                <VideocamIcon fontSize="small" color="disabled" />
                <Typography variant="caption" color="text.secondary">
                  Social media video content
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.7 }}>
                <VideoLibraryIcon fontSize="small" color="disabled" />
                <Typography variant="caption" color="text.secondary">
                  Voice + video synchronization
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Account Benefits Preview */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{accountType.charAt(0).toUpperCase() + accountType.slice(1)} Plan Benefits:</strong> 
              {videoQuotas.maxVideos === -1 ? ' Unlimited videos' : ` ${videoQuotas.maxVideos} videos/month`}, 
              up to {videoQuotas.maxDuration} seconds, {videoQuotas.maxResolution} resolution
            </Typography>
          </Alert>

          <Button
            variant="outlined"
            startIcon={<VideoIcon />}
            disabled={true}
            fullWidth
            sx={{ opacity: 0.6 }}
          >
            Video Generation (Coming Soon)
          </Button>
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            🎬 Get notified when video generation becomes available
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Audio Description Tool */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <AudioIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">Audio Description</Typography>
            {audioData && (
              <CheckIcon color="success" fontSize="small" sx={{ ml: 'auto' }} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Record or upload an audio description in any language. Our AI will automatically transcribe and translate to enhance your business profile.
          </Typography>
          
          {/* Multi-language support notice */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TranslateIcon fontSize="small" />
              <Typography variant="body2">
                <strong>Multi-language support:</strong> Record in your preferred language - we'll automatically translate for international customers.
              </Typography>
            </Box>
          </Alert>
          
          {audioData ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Audio description is active and helping improve your business visibility.
                </Typography>
              </Alert>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip 
                  label={`${Math.round(audioData.durationMinutes * 60)} sec`} 
                  size="small" 
                  color="primary" 
                />
                <Chip 
                  label={audioData.audioType?.toUpperCase() || 'Audio'} 
                  size="small" 
                  variant="outlined" 
                />
                <Chip 
                  label={audioData.processingStatus || 'Processing'} 
                  size="small" 
                  color={audioData.processingStatus === 'completed' ? 'success' : 'warning'}
                  variant="outlined" 
                />
                {audioData.language && (
                  <Chip 
                    label={audioData.language} 
                    size="small" 
                    color="info"
                    variant="outlined"
                    icon={<TranslateIcon fontSize="small" />}
                  />
                )}
              </Stack>
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                No audio description yet. Add one to help customers understand your business better.
              </Typography>
            </Alert>
          )}

          <Button
            variant={audioData ? "outlined" : "contained"}
            onClick={() => setOpenDialog('audio')}
            startIcon={<AudioIcon />}
            fullWidth
          >
            {audioData ? 'Update Audio Description' : 'Add Audio Description'}
          </Button>
        </AccordionDetails>
      </Accordion>

       {/* Smart Documents → navigation only */}
      <Accordion>
        <AccordionSummary
          expandIcon={null}
          onClick={() => navigate(
            `/business/${businessId}/knowledge-base`,
            { state: { businessName, returnPath: window.location.pathname } }
          )}
          sx={{ cursor: 'pointer' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <KnowledgeIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">Business Knowledge Base</Typography>
            <Box sx={{ ml: 'auto' }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/business/${businessId}/knowledge-base`,
                    { state: { businessName, returnPath: window.location.pathname } }
                  );
                }}
                title="Open full page"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </AccordionSummary>
      </Accordion>

      {/* Content Drafts → navigation only */}
      <Accordion>
        <AccordionSummary
          expandIcon={null}
          onClick={() => navigate(
            `/business/${businessId}/drafts`,
            { state: { businessName } }
          )}
          sx={{ cursor: 'pointer' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <DraftsIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">Content Drafts</Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {pendingDraftCount > 0 && (
                <Chip
                  label={pendingDraftCount}
                  size="small"
                  color="warning"
                  sx={{ height: 20, minWidth: 20, fontSize: '0.7rem' }}
                />
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/business/${businessId}/drafts`,
                    { state: { businessName } }
                  );
                }}
                title="Open drafts"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </AccordionSummary>
      </Accordion>

      <SocialMediaManager
        businessId={businessId}
        businessName={businessName}
        isBusinessOwner={isBusinessOwner}
        accountType={accountType}
        navigationOnly={true}   
        currentGooglePlaceId={googlePlaceId}
        onOpenFullPage={() => navigate(
          `/business/${businessId}/social/accounts`,
          { state: { businessName, returnPath: window.location.pathname } }
        )}
      />


      {/* Coming Soon Tools - Updated */}
      <Accordion disabled>
        <AccordionSummary>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <AIIcon color="disabled" fontSize="small" />
            <Typography variant="subtitle2" color="text.disabled">
              More AI Tools
            </Typography>
            <Chip 
              label="COMING SOON" 
              size="small" 
              color="default" 
              variant="outlined" 
              sx={{ ml: 'auto' }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Additional AI-powered tools for business automation and customer engagement.
          </Typography>
          
          <Stack spacing={1} sx={{ opacity: 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AIIcon fontSize="small" color="disabled" />
              <Typography variant="caption" color="text.secondary">
                AI Chatbot Integration
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AIIcon fontSize="small" color="disabled" />
              <Typography variant="caption" color="text.secondary">
                Email Marketing Automation
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AIIcon fontSize="small" color="disabled" />
              <Typography variant="caption" color="text.secondary">
                Review Response Generator
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Voice Cloning Dialog */}
      <Dialog 
        open={openDialog === 'voice'} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Voice Cloning & TTS</Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <VoiceCloningTool
            businessId={businessId}
            businessName={businessName}
            isBusinessOwner={isBusinessOwner}
            accountType={accountType}
            voiceLibrary={voiceLibrary}
            quotaUsage={quotaUsage}
            existingVoice={existingVoice}
            onVoiceSuccess={handleVoiceSuccess}
            onCancel={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>

      {/* Audio Upload Dialog */}
      <Dialog 
        open={openDialog === 'audio'} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Audio Description Tool</Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <BusinessAudioUpload
            businessId={businessId}
            businessName={businessName}
            existingAudio={audioData}
            onSuccess={handleAudioSuccess}
            onCancel={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog 
        open={openDialog === 'document'} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Smart Documents</Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <BusinessDocumentUpload
            businessId={businessId}
            businessName={businessName}
            existingDocuments={documents}
            onSuccess={handleDocumentSuccess}
            onCancel={handleCloseDialog}
            quotas={documentQuotas}
            accountType={accountType}
          />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default AIToolsSection;










